import { useCallback, useRef } from 'react';

/**
 * Recharts shows its touch tooltip on ANY `touchmove` over the chart's SVG —
 * it has no notion of "the user is scrolling the PAGE, not inspecting the
 * chart" (a scroll gesture that merely starts on/passes over the chart
 * triggers it same as a deliberate drag), and nothing clears it on
 * `touchend`/`touchcancel`, so a scroll-through leaves the tooltip stuck open
 * (see recharts' `RechartsWrapper` — only `touchmove` dispatches the
 * touch-tooltip action; only `onMouseLeave` clears tooltip state).
 *
 * This restores the expected mobile behaviour, no `preventDefault` (page
 * scroll must keep working):
 *  - `touchmove` is intercepted in the CAPTURE phase and stopped from ever
 *    reaching recharts until the gesture proves itself a deliberate
 *    HORIZONTAL inspect-drag — so a scroll never opens a tooltip at all,
 *    not even for one frame (closing after the fact flickered);
 *  - a gesture that turned out to be a vertical scroll stays suppressed for
 *    the rest of that touch (a scroll doesn't reverse into an inspect
 *    mid-gesture), and force-closes anything already open;
 *  - lifting the finger (touchend/touchcancel) ALWAYS closes the tooltip,
 *    matching "disappear as soon as the finger is released" even for a
 *    genuine inspect-drag.
 *
 * Suppression only stops the TOUCH event's propagation — native scrolling
 * isn't propagation-based and pointer events are a separate stream, so page
 * scroll, the photo-strip swipe and the marquee (pointer events) all keep
 * working underneath.
 *
 * Closing is done by firing a synthetic `mouseout` (with a `relatedTarget`
 * outside the chart) at recharts' own `.recharts-wrapper` node — the native
 * event React's synthetic `onMouseLeave` is actually built on (mouseleave
 * itself doesn't bubble, so React can't delegate it from the root; a real
 * `mouseleave` event here would silently do nothing) — rather than reaching
 * into recharts' internal state.
 *
 * Spread the returned handlers onto whichever DOM node wraps the chart (an
 * ancestor of `.recharts-wrapper` is enough; it's looked up lazily off
 * `currentTarget` so no ref plumbing is needed).
 */
export function useChartTouchTooltipGuard() {
  const start = useRef<{ x: number; y: number } | null>(null);
  // 'undecided' → suppress; 'inspect' → let recharts track; 'scroll' →
  // suppress for the rest of the touch.
  const gesture = useRef<'undecided' | 'inspect' | 'scroll'>('undecided');

  const closeTooltip = (root: HTMLElement) => {
    // ALL charts under the guarded node — in grid modes («все стеллажи»,
    // the week grid) a scroll passes over many charts and every touched
    // tooltip must close, not just the first one's.
    for (const wrapper of root.querySelectorAll<HTMLElement>('.recharts-wrapper')) {
      // React doesn't attach a native `mouseleave` listener at all — it
      // SIMULATES onMouseEnter/onMouseLeave from bubbling `mouseover`/
      // `mouseout` (mouseleave/mouseenter don't bubble, so React's root-level
      // delegation can't use them directly). A synthetic `mouseleave` is
      // therefore a no-op; `mouseout` with a `relatedTarget` outside the
      // wrapper is what actually reaches recharts' `onMouseLeave` prop.
      wrapper.dispatchEvent(
        new MouseEvent('mouseout', { bubbles: true, cancelable: true, relatedTarget: document.body }),
      );
    }
  };

  const onTouchStart = useCallback((e: React.TouchEvent<HTMLElement>) => {
    const t = e.touches[0];
    start.current = t ? { x: t.clientX, y: t.clientY } : null;
    gesture.current = 'undecided';
  }, []);

  const onTouchMoveCapture = useCallback((e: React.TouchEvent<HTMLElement>) => {
    const s = start.current;
    const t = e.touches[0];
    if (gesture.current === 'undecided' && s && t) {
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      // Thresholds so sub-pixel jitter of a settling finger doesn't decide
      // the gesture prematurely.
      if (Math.abs(dy) > 6 && Math.abs(dy) > Math.abs(dx)) gesture.current = 'scroll';
      else if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) gesture.current = 'inspect';
    }
    if (gesture.current !== 'inspect') {
      // Capture-phase stop: recharts (a bubble-phase listener below) never
      // sees this touchmove, so no tooltip opens in the first place.
      e.stopPropagation();
      if (gesture.current === 'scroll') closeTooltip(e.currentTarget);
    }
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent<HTMLElement>) => {
    closeTooltip(e.currentTarget);
    start.current = null;
    gesture.current = 'undecided';
  }, []);

  return {
    onTouchStart,
    onTouchMoveCapture,
    onTouchEnd,
    // Cancel (e.g. an OS gesture takes over) must close it too — same as a
    // normal release, nothing left dangling.
    onTouchCancel: onTouchEnd,
  };
}
